/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { CombinedFileScope } from './CombinedFileScope';
import { useAttachmentsValidations } from '../../../hooks/useAttachmentsValidations';
import {
  isAttachmentTypeUploaded,
  isAttachmentInputHidden,
  isCombinedTypeHidden,
} from '../../../services/attachments.helper';

const useStyles = makeStyles(theme => ({
  titleText: {
    color: theme.palette.titleText,
  },
  errorAlert: {
    border: `solid 1px ${theme.palette.error.main}`,
  },
}));

export function CombinedFile({
  attachmentsMetadata,
  form,
  parentClasses,
  readOnly,
  attachmentTypesConfig,
  includedAttachmentTypes,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { gapsValidation, overlapValidation, attachmentsConfigReady } = useAttachmentsValidations();

  const getTypes = useMemo(() => {
    return attachmentTypesConfig
      ?.filter(type => includedAttachmentTypes.indexOf(type.type) >= 0)
      .map(type => type.type);
  }, [attachmentTypesConfig, includedAttachmentTypes]);

  const getValues = useMemo(() => {
    return attachmentTypesConfig
      ?.map(type => [type.type])
      .reduce((acc, type) => [...acc, `${type}from`, `${type}to`], []);
  }, [attachmentTypesConfig]);

  const gapsValidate = gapsValidation({ form });
  const overlapsValidate = overlapValidation({ form });
  const { trigger, errors } = form;

  useEffect(() => {
    (async () => {
      await trigger(['gap', 'overlap']);
    })();
  }, [trigger, ...Object.values(getValues)]);

  return (
    attachmentsConfigReady && (
      <Box>
        <Typography variant="subtitle1" component="span" gutterBottom className={classes.titleText}>
          {t('attachments.select.types')}
        </Typography>
        {getTypes.map((type) => {
          if (isCombinedTypeHidden(attachmentTypesConfig, type)) {
            return null;
          }
          return (
            <CombinedFileScope
              key={type}
              type={type}
              form={form}
              parentClasses={parentClasses}
              hiddenInputs={isAttachmentInputHidden(attachmentTypesConfig, type)}
              disabled={isAttachmentTypeUploaded(attachmentsMetadata, type)}
              readOnly={readOnly}
            />
          );
        })}
        <input type="hidden" required={true} name="overlap" ref={form.register(overlapsValidate)} />
        <input type="hidden" required={true} name="gap" ref={form.register(gapsValidate)} />
        {errors?.overlap && (
          <Alert data-testid="overlap-error" severity="error" className={classes.errorAlert}>
            {t(errors.overlap?.message)}
          </Alert>
        )}
        {errors?.gap && (
          <Alert data-testid="gap-error" severity="error" className={classes.errorAlert}>
            {t(errors.gap?.message)}
          </Alert>
        )}
      </Box>
    )
  );
}
